import { Controller } from "@hotwired/stimulus"

// Schedule-report modal — cadence picker + recipients chip input +
// content toggles. On submit POSTs JSON to create the subscription, then
// closes with a success toast. Keeping the frontend lightweight: the
// cadence picker toggles which day-of-* select shows, the chip input
// is just `input + keydown Enter/comma/blur → commit`.
export default class extends Controller {
  static targets = [
    "errors",
    "name", "frequencyBtn",
    "dayOfWeek", "dayOfMonth", "dayWeekWrap", "dayMonthWrap",
    "timeOfDay", "timezone",
    "recipientChips", "recipientInput",
    "filterPreset",
    "optCharts", "optAi", "optTable", "contentPreset",
    "submitBtn", "submitLabel"
  ]
  static values = { createUrl: String }

  connect() {
    this._frequency = "weekly"
    this._recipients = []
    this._detectTimezone()
  }

  _detectTimezone() {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (tz && this.hasTimezoneTarget) {
        const opt = Array.from(this.timezoneTarget.options).find(o => o.value === tz)
        if (opt) this.timezoneTarget.value = tz
      }
    } catch (_e) { /* fallback to server default */ }
  }

  // Phase 2.12 Tier 5 Step 44 — newsletter preset defaults AI summary off.
  // Admin can manually re-check if they want a Pulse paragraph inside the
  // newsletter shell.
  contentPresetChanged(event) {
    if (!this.hasOptAiTarget) return
    const preset = event.currentTarget.value
    if (preset === "newsletter") this.optAiTarget.checked = false
  }

  selectFrequency(event) {
    event.preventDefault()
    this._frequency = event.currentTarget.dataset.frequency
    this.frequencyBtnTargets.forEach(b => {
      const selected = b.dataset.frequency === this._frequency
      b.classList.toggle("border-[var(--portfolio-primary)]", selected)
      b.classList.toggle("text-[var(--portfolio-primary)]", selected)
      b.classList.toggle("bg-[var(--portfolio-primary)]/5", selected)
      b.classList.toggle("font-medium", selected)
      b.classList.toggle("border-gray-200", !selected)
      b.classList.toggle("text-gray-600", !selected)
      b.classList.toggle("hover:bg-gray-50", !selected)
    })
    this.dayWeekWrapTarget.classList.toggle("hidden", this._frequency !== "weekly")
    this.dayMonthWrapTarget.classList.toggle("hidden", this._frequency !== "monthly")
  }

  focusRecipientInput() {
    this.recipientInputTarget.focus()
  }

  recipientKeydown(event) {
    if (event.key === "Enter" || event.key === "," || event.key === ";") {
      event.preventDefault()
      this._commitRecipient()
    } else if (event.key === "Backspace" && this.recipientInputTarget.value === "" && this._recipients.length) {
      this._recipients.pop()
      this._renderChips()
    }
  }

  recipientBlur() {
    if (this.recipientInputTarget.value.trim()) this._commitRecipient()
  }

  _commitRecipient() {
    const raw = this.recipientInputTarget.value.trim()
    if (!raw) return
    const parts = raw.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
    parts.forEach(p => {
      if (!this._recipients.includes(p)) this._recipients.push(p)
    })
    this.recipientInputTarget.value = ""
    this._renderChips()
  }

  _removeRecipient(email) {
    this._recipients = this._recipients.filter(e => e !== email)
    this._renderChips()
  }

  _renderChips() {
    // Clear then rebuild before the input.
    const input = this.recipientInputTarget
    Array.from(this.recipientChipsTarget.querySelectorAll("[data-chip]")).forEach(el => el.remove())
    this._recipients.forEach(email => {
      const chip = document.createElement("span")
      chip.dataset.chip = "true"
      chip.className = "inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 border border-gray-200 rounded-full text-[13px] text-gray-800"
      chip.textContent = email
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "text-gray-400 hover:text-gray-700 text-[14px] leading-none cursor-pointer"
      btn.textContent = "×"
      btn.addEventListener("click", () => this._removeRecipient(email))
      chip.appendChild(btn)
      this.recipientChipsTarget.insertBefore(chip, input)
    })
  }

  backToExport(event) {
    event.preventDefault()
    this.element.classList.add("hidden")
    document.body.style.overflow = ""
    const exportModal = document.getElementById("portfolio-export-modal")
    if (exportModal) {
      exportModal.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    }
  }

  async submit(event) {
    event.preventDefault()
    if (this.submitBtnTarget.disabled) return

    if (this.recipientInputTarget.value.trim()) this._commitRecipient()

    this._clearErrors()

    const payload = {
      name: this.nameTarget.value.trim(),
      frequency: this._frequency,
      day_of_week: this._frequency === "weekly" ? parseInt(this.dayOfWeekTarget.value, 10) : null,
      day_of_month: this._frequency === "monthly" ? parseInt(this.dayOfMonthTarget.value, 10) : null,
      time_of_day: this.timeOfDayTarget.value,
      timezone: this.timezoneTarget.value,
      recipients: this._recipients,
      filter_preset_id: this.filterPresetTarget.value || null,
      options: {
        include_charts: this.optChartsTarget.checked,
        include_ai_summary: this.hasOptAiTarget ? this.optAiTarget.checked : false,
        include_table: this.optTableTarget.checked,
        trend_range: 6,
        content_preset: this.hasContentPresetTarget ? this.contentPresetTarget.value : "default"
      }
    }

    this._renderBusyState()

    try {
      const res = await this._postJson(this.createUrlValue, payload)
      if (res.success) {
        this._showToast("Scheduled report created", "notice")
        this._closeAll()
        // Mixpanel "Portfolio Report Scheduled" is tracked server-side in the
        // controller (see PortfolioScheduledReportsController#create) so both
        // the modal form and any future API caller get the same event.
        document.dispatchEvent(new CustomEvent("portfolio-scheduled-reports-manage:open"))
      } else {
        this._renderErrors(res.errors || ["Could not schedule this report."])
      }
    } catch (err) {
      console.error("Schedule report failed", err)
      this._renderErrors(["Network error — please try again."])
    } finally {
      this._resetBusyState()
    }
  }

  _postJson(url, body) {
    const csrf = document.querySelector("meta[name=csrf-token]")?.content
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrf || ""
      },
      body: JSON.stringify(body),
      credentials: "same-origin"
    }).then(r => r.json())
  }

  _renderBusyState() {
    this.submitBtnTarget.disabled = true
    this.submitLabelTarget.textContent = "Scheduling…"
  }
  _resetBusyState() {
    this.submitBtnTarget.disabled = false
    this.submitLabelTarget.textContent = "Schedule"
  }

  _renderErrors(list) {
    this.errorsTarget.textContent = list.join(" · ")
    this.errorsTarget.classList.remove("hidden")
  }
  _clearErrors() {
    this.errorsTarget.textContent = ""
    this.errorsTarget.classList.add("hidden")
  }

  _closeAll() {
    this.element.classList.add("hidden")
    document.body.style.overflow = ""
  }

  _showToast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }
}
